import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthState, LoginCredentials, User, AdminUserRegistration, CompleteRegistration } from '../interfaces/auth';
import { authService } from '../services/auth';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  registerUser: (userData: AdminUserRegistration) => Promise<any>;
  completeRegistration: (userData: CompleteRegistration) => Promise<void>;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: true,
  error: null,
  needsProfileCompletion: false,
};

// Create context with default undefined value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create provider component
function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const storedToken = localStorage.getItem('auth_token');
        
        if (storedToken) {
          const user = await authService.getCurrentUser();
          
          if (user) {
            const needsProfileCompletion = user.isRegistrationComplete === false;
            
            // Atualizar localStorage com dados do usuário
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('user_role', user.role || '');
            
            setState({
              isAuthenticated: true,
              user,
              token: storedToken,
              loading: false,
              error: null,
              needsProfileCompletion,
            });
          } else {
            // Token existe mas usuário não foi encontrado
            localStorage.removeItem('auth_token');
            setState({
              ...initialState,
              loading: false,
            });
          }
        } else {
          setState({
            ...initialState,
            loading: false,
          });
        }
      } catch (error) {
        setState({
          ...initialState,
          loading: false,
          error: 'Erro ao inicializar autenticação',
        });
      }
    };

    init();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      setState({
        ...state,
        loading: true,
        error: null,
      });

      const { user, token } = await authService.login(credentials);
      
      // Verifica se o usuário precisa completar o perfil
      const needsProfileCompletion = user.isRegistrationComplete === false;
      
      // Salva no localStorage
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('user_role', user.role || '');

      setState({
        isAuthenticated: true,
        user,
        token,
        loading: false,
        error: null,
        needsProfileCompletion,
      });
    } catch (error) {
      setState({
        ...state,
        loading: false,
        error: (error as Error).message || 'Erro ao realizar login',
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      setState({
        ...state,
        loading: true,
      });

      await authService.logout();

      // Limpar localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('user_role');

      setState({
        ...initialState,
        loading: false,
      });
    } catch (error) {
      setState({
        ...state,
        loading: false,
        error: 'Erro ao realizar logout',
      });
    }
  };

  const registerUser = async (userData: AdminUserRegistration): Promise<any> => {
    try {
      return await authService.registerUser(userData);
    } catch (error) {
      setState({
        ...state,
        error: (error as Error).message || 'Erro ao registrar usuário',
      });
      throw error;
    }
  };

  const completeRegistration = async (userData: CompleteRegistration): Promise<void> => {
    try {
      setState({
        ...state,
        loading: true,
        error: null,
      });

      // Fazer a requisição para completar o registro
      const { user, token } = await authService.completeRegistration(userData);
      
      // Atualizar localStorage
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('user_role', user.role || '');
      
      setState({
        isAuthenticated: true,
        user,
        token,
        loading: false,
        error: null,
        needsProfileCompletion: false,
      });
    } catch (error: any) {
      console.error('AuthContext - Erro ao completar registro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      setState({
        ...state,
        loading: false,
        error: (error as Error).message || 'Erro ao completar o registro',
      });
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        registerUser,
        completeRegistration,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Create hook to use the auth context
function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

export { AuthProvider, useAuth };
